import { pascalCase } from 'module/helpers/pascal-case';
import { unreachable } from 'module/helpers/unreachable';
import { SingleUserField } from 'module/types/filter-field/field/filterable-field';
import { kebabCase } from 'lodash';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from '../separators';

interface SingleUserFieldFilterBase {
  field: SingleUserField;
}

interface SingleUserFieldValueFilterBase extends SingleUserFieldFilterBase {
  value?: number;
}

interface SingleUserFieldValuesFilterBase extends SingleUserFieldFilterBase {
  value?: number[];
}

interface SingleUserIsFilter extends SingleUserFieldValueFilterBase {
  type: SelectFilterOperationType.Is;
}

interface SingleUserIsNotFilter extends SingleUserFieldValueFilterBase {
  type: SelectFilterOperationType.IsNot;
}

interface SingleUserIsOneOfFilter extends SingleUserFieldValuesFilterBase {
  type: SelectFilterOperationType.IsOneOf;
}

interface SingleUserIsNoneOfFilter extends SingleUserFieldValuesFilterBase {
  type: SelectFilterOperationType.IsNoneOf;
}

interface SingleUserIsEmptyFilter extends SingleUserFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface SingleUserIsNotEmptyFilter extends SingleUserFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type SingleUserFieldFilter =
  | SingleUserIsFilter
  | SingleUserIsNotFilter
  | SingleUserIsOneOfFilter
  | SingleUserIsNoneOfFilter
  | SingleUserIsEmptyFilter
  | SingleUserIsNotEmptyFilter;

const isValid = (filter: SingleUserFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
      return filter.value != null;
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

const to = valueToString<number>((x) => x.toString());
const from = valueFromString((x) => parseInt(x, 10));

const toString = (filter: SingleUserFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
      return [type, to(filter.value == null ? [] : [filter.value])].join(
        TermSeparator,
      );
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (
  value: string,
  field: SingleUserField,
): SingleUserFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
      return { field, type, value: (from(meta) ?? [])[0] };
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const SingleUserFieldFilter = {
  isValid,
  toString,
  parse,
};
